/**
 * The other way out of the room: a competitor becomes something watched.
 *
 * `PromoteIdea`'s twin. That one turns an idea into a subject you write about;
 * this one turns a company into a subject you keep an eye on, whose site is
 * captured on a cadence and whose changes reach a feed. The watch outlives the
 * room — archiving the room leaves it running.
 *
 * TWO WAYS TO START, because a capture costs a fetch: one starts watching now,
 * the other creates it paused. The result says which way it went.
 *
 * A COMPANY WITH NO WEBSITE IS NOT AN ERROR TO FIX BY HAND. The server answers
 * that case with a sentence pointing at the dig, because a company missing its
 * url is already the "site" hole the profiler seat is woken for — it fills that
 * in itself. So this component shows that sentence and offers the url field only
 * as a shortcut, rather than leading with a form.
 */
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../../services/api';
import ShellLink from '../common/ShellLink';
import { watchSubjectHref } from '../../pages/watchFeed';
import type { WatchPreview, WatchTrackResult } from '../../types';

export default function TrackThis({
  boardroomId,
  itemId,
  watchSubjectId = null,
  hasUrl,
  preview = null,
  returnLabel,
  compact = false,
}: {
  boardroomId: number;
  itemId: number;
  /** Set when this company is already watched. */
  watchSubjectId?: number | null;
  /** Whether the room knows this company's website. */
  hasUrl: boolean;
  /** What a watch would already find. Turns the button from a promise into a specific reason. */
  preview?: WatchPreview | null;
  /** What to call this room on the way back. `returnPath.ts` is emphatic that a trail must name
   *  the PAGE rather than the app — "Back to Skate brands", not "Back to Lumnian". */
  returnLabel?: string;
  compact?: boolean;
}) {
  // The exact url being read, so the way back lands on the profile you were on rather than the
  // room's default view.
  const location = useLocation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<WatchTrackResult | null>(null);
  const [url, setUrl] = useState('');
  const [showUrl, setShowUrl] = useState(false);

  const subjectId = result?.subject_id ?? watchSubjectId;

  if (subjectId) {
    return (
      <div
        className="text-xs text-emerald-800 dark:text-emerald-300"
        data-testid={`tracked-${itemId}`}
      >
        {result?.note ?? 'This company is watched.'}{' '}
        {result && !result.is_active && (
          <span className="text-amber-700 dark:text-amber-400">
            It is paused, so nothing is checked until you turn it on.{' '}
          </span>
        )}
        {/* CARRIES THE WAY BACK. `PageHeader` on the watch page already renders `ReturnTrail`, so
            stamping `?from=` here is the whole of what makes a back button appear there — and
            without it, following this link is the dead end `returnPath.ts` was written about. */}
        <ShellLink
          to={watchSubjectHref(
            subjectId,
            `${location.pathname}${location.search}`,
            returnLabel,
          )}
          className="font-semibold underline"
        >
          Open the watch
        </ShellLink>
      </div>
    );
  }

  const go = async (activate: boolean) => {
    setBusy(true);
    setError(null);
    try {
      setResult(
        await api.trackBoardroomCompany(boardroomId, itemId, {
          url: url.trim() || null,
          activate,
        }),
      );
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Could not start watching this';
      setError(message);
      // The server's refusal for a url-less company names the dig. Offering the field only after
      // that refusal keeps the normal path a single press.
      if (/website/i.test(message)) setShowUrl(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2" data-testid={`track-${itemId}`}>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <button
          type="button"
          disabled={busy}
          onClick={() => go(true)}
          className="px-2 py-1 rounded border border-gray-300 bg-gray-100 text-gray-800 hover:bg-gray-200 disabled:opacity-40 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
          title="Starts checking their site on a cadence and tells you what changes"
        >
          Watch this company
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => go(false)}
          className="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40"
          title="Sets it up without fetching anything yet"
        >
          Add it paused
        </button>
        {!hasUrl && !showUrl && !compact && (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            The room has not found their website yet - a dig fills that in.
          </span>
        )}
      </div>

      {/* WHY BOTHER. Without this the button promises alerts about a future nobody can see, which
          is a thing people leave alone — and a monitor nobody switches on is worth nothing. This
          is read from pages the room has already crawled, so it costs no fetch. */}
      {hasUrl && preview?.call_to_action && !compact && (
        <p
          className={`text-xs ${
            preview.has_anything
              ? 'text-gray-600 dark:text-gray-300'
              : 'text-gray-400 dark:text-gray-500'
          }`}
          data-testid={`track-why-${itemId}`}
        >
          {preview.call_to_action}
        </p>
      )}

      {/* WHERE THEY ASK. Links to the pages the form was seen on, so you can go and look at it
          yourself — the home page first when we read it, since the form is usually site-wide. */}
      {hasUrl && !compact && !!preview?.capture_pages?.length && (
        <div
          className="text-xs text-gray-600 dark:text-gray-300 space-y-0.5"
          data-testid={`track-capture-${itemId}`}
        >
          <div>
            <span className="font-medium">Email provider:</span>{' '}
            {preview.capture_vendor_names.length
              ? preview.capture_vendor_names.join(', ')
              : 'not identified'}
          </div>
          <div className="flex flex-wrap gap-x-3 gap-y-0.5">
            <span className="font-medium">Email capture:</span>
            {preview.capture_pages.map((p) => (
              <a
                key={p.url}
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-gray-900 dark:hover:text-white"
                title={p.url}
              >
                {p.where} on {shortUrl(p.url)}
                {p.offer ? ` (${p.offer})` : ''}
              </a>
            ))}
          </div>
        </div>
      )}

      {showUrl && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://their-site.com"
            className="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-xs w-56"
            aria-label="Their website"
          />
          <span className="text-xs text-gray-500 dark:text-gray-400">
            Or dig into this company and the profiler will find it.
          </span>
        </div>
      )}

      {error && (
        <p className="text-xs text-red-600 dark:text-red-400" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/** "a.com/pricing" — the host without www and the path, for a link's text. */
function shortUrl(url: string): string {
  try {
    const u = new URL(url);
    return `${u.hostname.replace(/^www\./, '')}${u.pathname === '/' ? '/' : u.pathname}`;
  } catch {
    return url;
  }
}
